/**
 * The global frame-sampler observer (ARD-1977).
 *
 * One set of listeners on `document` sees every scroller, every layout
 * transition and every primary-button or navigation-key interaction in the
 * app, so no component registers itself. Scroll events do not bubble but do
 * reach the document in the capture phase; the others bubble.
 *
 * Interactions start at the INPUT, not at `click` (ARD-1977 accuracy, A1).
 * Activation commonly runs before any click: Radix Tabs switch in
 * `onMouseDown`, and in `onFocus` when arrow keys move between tabs. An
 * episode opened on `click` began after the activation render it existed to
 * measure (WebKit read a 120 ms tab switch as ~3 ms) and keyboard navigation
 * opened nothing at all. So a trusted primary-button `pointerdown` or a
 * trusted navigation `keydown` opens the episode, at that handler's
 * `performance.now()` — never `event.timeStamp`, which WebKit does not report
 * in that clock — and `click` is not observed, so the click after a
 * pointerdown can neither open nor extend anything. The element that fired the event names
 * the episode (`describeFrameTarget`): a `data-frame-surface` marker, else
 * the tag name, nothing derived from runtime data.
 *
 * Admission first, work second. `canAdmitEpisode` is a few comparisons; only
 * when it says yes does the observer walk the DOM for a label and build a
 * sampler. At the production gap almost every event is refused, and a refused
 * event must cost nothing more than the listener call.
 *
 * One episode at a time: the first admitted signal binds the episode to its
 * element and trigger, later signals from the same element refresh it, and
 * signals from any other element are ignored until it closes. An interaction
 * is the exception: it TAKES OVER an open scroll or transition episode,
 * inheriting its admission rather than asking for a new one (which the
 * production gap would refuse), so the interaction the user chose is the one
 * measured and the work budget is unchanged.
 *
 * A transition episode tracks the SET of admitted properties on its element
 * (`width` and `height` may run together) and ends when that set empties. An
 * end event for a property that was never admitted (`opacity` finishing early
 * on the same element) does not close it.
 *
 * Not observable from here, and out of scope: JS-driven animations (`motion`,
 * WAAPI) emit no DOM events, and CSS keyframe animations are excluded because
 * an infinite spinner would hold the rate limit hostage.
 */
import { describeFrameTarget } from './describe-target';
import { createFrameSampler, type FrameSampler } from './frame-sampler';
import { canAdmitEpisode, tryAdmitEpisode } from './gate';
import type { FrameTrigger } from './surfaces';

export const LAYOUT_TRANSITION_PROPERTIES: ReadonlySet<string> = new Set([
  'width',
  'height',
  'min-width',
  'max-width',
  'min-height',
  'max-height',
  'flex',
  'flex-grow',
  'flex-shrink',
  'flex-basis',
  'transform',
  'translate',
  'scale',
  'grid-template-columns',
  'grid-template-rows',
  'left',
  'right',
  'top',
  'bottom',
  'inset',
  'inset-inline-start',
  'inset-inline-end',
  'margin-left',
  'margin-right',
  'margin-inline-start',
  'margin-inline-end',
]);

/**
 * Keys that navigate or activate (tabs, menus, lists, dialogs). Ordinary typing
 * is a different metric with a different volume and opens nothing.
 */
export const INTERACTION_KEYS: ReadonlySet<string> = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Enter',
  ' ',
  'Tab',
  'Escape',
  'Home',
  'End',
  'PageUp',
  'PageDown',
]);

/** Same-label transitions closer together than this are ignored. */
export const TRANSITION_REPEAT_GAP_MS = 2_000;

/** The per-label transition clock is cleared past this many labels. */
const MAX_TRACKED_TRANSITION_LABELS = 64;

type ActiveEpisode = {
  sampler: FrameSampler;
  target: EventTarget;
  trigger: FrameTrigger;
  /** `${property}@${pseudoElement}` keys still running; transition episodes only. */
  transitions: Set<string>;
};

const defaultIsTrustedInput = (event: Event): boolean => event.isTrusted;

/**
 * Whether an input event came from the user rather than a script. jsdom marks
 * every dispatched event untrusted and `isTrusted` cannot be redefined on an
 * event, so the suites swap this predicate; production never does.
 */
let isTrustedInput = defaultIsTrustedInput;

/** Test seam (design D3). `null` restores the real `isTrusted` check. */
export function __setFrameSamplerTrustForTests(
  predicate: ((event: Event) => boolean) | null
): void {
  isTrustedInput = predicate ?? defaultIsTrustedInput;
}

const transitionKey = (event: Event): string => {
  const { propertyName, pseudoElement } = event as TransitionEvent;
  return `${propertyName}@${pseudoElement ?? ''}`;
};

export function installFrameSamplerObserver(root: Document = document): () => void {
  let active: ActiveEpisode | null = null;
  const lastTransitionByLabel = new Map<string, number>();

  // Checked BEFORE admission. The sampler refuses to open on a hidden
  // document anyway, but by then the admission would already be spent and
  // the next foreground interaction refused for the whole gap. A scroll or a
  // layout transition run by script on a backgrounded tab is such an event.
  const hidden = () => root.visibilityState === 'hidden';

  const current = (): ActiveEpisode | null => {
    if (active && !active.sampler.isOpen()) active = null;
    return active;
  };

  /** Open a new episode for `target`, admission already granted or inherited. */
  const start = (target: EventTarget, trigger: FrameTrigger, key?: string) => {
    const sampler = createFrameSampler(describeFrameTarget(target), trigger);
    sampler.open();
    if (!sampler.isOpen()) return;
    active = { sampler, target, trigger, transitions: new Set(key ? [key] : []) };
  };

  const onScroll = (event: Event) => {
    const target = event.target;
    if (!target) return;
    const open = current();
    if (open) {
      if (open.target === target && open.trigger === 'scroll') open.sampler.touch();
      return;
    }
    if (hidden()) return;
    const t = performance.now();
    if (!tryAdmitEpisode(t)) return;
    start(target, 'scroll');
  };

  const isInteraction = (trigger: FrameTrigger) =>
    trigger === 'pointer' || trigger === 'key';

  const onInteraction = (target: EventTarget | null, trigger: 'pointer' | 'key') => {
    if (!target) return;
    const open = current();
    if (open) {
      if (isInteraction(open.trigger)) return;
      // Take over: the open episode's admission carries to the interaction.
      open.sampler.abort();
      active = null;
      start(target, trigger);
      return;
    }
    if (hidden()) return;
    const t = performance.now();
    if (!tryAdmitEpisode(t)) return;
    start(target, trigger);
  };

  const onPointerDown = (event: Event) => {
    if (!isTrustedInput(event) || (event as PointerEvent).button !== 0) return;
    onInteraction(event.target, 'pointer');
  };

  const onKeyDown = (event: Event) => {
    const { key, isComposing } = event as KeyboardEvent;
    if (!isTrustedInput(event) || isComposing || !INTERACTION_KEYS.has(key)) return;
    onInteraction(event.target, 'key');
  };

  const onTransitionStart = (event: Event) => {
    const property = (event as TransitionEvent).propertyName;
    if (!LAYOUT_TRANSITION_PROPERTIES.has(property)) return;
    const target = event.target;
    if (!target) return;

    const open = current();
    if (open) {
      // A second admitted property on the episode's own element joins it.
      if (open.target === target && open.trigger === 'transition') {
        open.transitions.add(transitionKey(event));
        open.sampler.touch();
      }
      return;
    }

    if (hidden()) return;
    const t = performance.now();
    if (!canAdmitEpisode(t)) return; // cheap; no DOM walk for a refused event

    const label = describeFrameTarget(target);
    const last = lastTransitionByLabel.get(label);
    if (last !== undefined && t - last < TRANSITION_REPEAT_GAP_MS) return;
    if (lastTransitionByLabel.size >= MAX_TRACKED_TRANSITION_LABELS)
      lastTransitionByLabel.clear();
    lastTransitionByLabel.set(label, t);

    if (!tryAdmitEpisode(t)) return;
    start(target, 'transition', transitionKey(event));
  };

  const onTransitionEnd = (event: Event) => {
    const open = current();
    if (!open || open.trigger !== 'transition' || open.target !== event.target) return;
    // Only a property this episode admitted can close it; `opacity` finishing
    // on the same element while `width` is still sliding does not.
    if (!open.transitions.delete(transitionKey(event))) return;
    if (open.transitions.size > 0) return;
    open.sampler.end();
    active = null;
  };

  root.addEventListener('scroll', onScroll, { capture: true, passive: true });
  root.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
  root.addEventListener('keydown', onKeyDown, { capture: true, passive: true });
  root.addEventListener('transitionstart', onTransitionStart, true);
  root.addEventListener('transitionend', onTransitionEnd, true);
  root.addEventListener('transitioncancel', onTransitionEnd, true);

  return () => {
    root.removeEventListener('scroll', onScroll, { capture: true });
    root.removeEventListener('pointerdown', onPointerDown, { capture: true });
    root.removeEventListener('keydown', onKeyDown, { capture: true });
    root.removeEventListener('transitionstart', onTransitionStart, true);
    root.removeEventListener('transitionend', onTransitionEnd, true);
    root.removeEventListener('transitioncancel', onTransitionEnd, true);
    current()?.sampler.abort();
    active = null;
    lastTransitionByLabel.clear();
  };
}
